import React, { useCallback, useEffect, useState } from 'react'
import { Button } from 'antd'
import { Link } from 'react-router-dom'
import { NAV_LINK_NAMES } from 'shared/consts'
import { useLanguage } from 'context/LanguageContext'
import { isPrerender } from 'shared/seo/prerender'
import { translations } from './translations'
import s from './CookieConsent.module.scss'

/**
 * Согласие на cookie.  Необходимые cookie (вход, язык, настройки) работают
 * всегда; аналитика — Яндекс.Метрика с вебвизором (запись сессий) —
 * загружается ТОЛЬКО после «Принять все».  Раньше счётчик стоял прямо в
 * public/index.html и загружался всем без согласия.
 */

type Choice = 'all' | 'necessary'

const STORAGE_KEY = 'rc_cookie_consent'
const CONSENT_VERSION = 1
const OPEN_EVENT = 'rc:cookie-settings'
const METRIKA_ID = 83746882

const readChoice = (): Choice | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const data = JSON.parse(raw)
        if (data?.v !== CONSENT_VERSION) return null
        return data.choice === 'all' || data.choice === 'necessary'
            ? data.choice
            : null
    } catch {
        return null
    }
}

const saveChoice = (choice: Choice) => {
    try {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ choice, v: CONSENT_VERSION, ts: Date.now() })
        )
    } catch {
        // Хранилище недоступно (приватный режим) — выбор действует до
        // перезагрузки страницы.
    }
}

type MetrikaWindow = Window & {
    ym?: ((...args: unknown[]) => void) & { a?: unknown[]; l?: number }
    __rcMetrikaLoaded?: boolean
}

/** Тот же официальный сниппет Метрики, но запускается только по согласию. */
const loadMetrika = () => {
    const w = window as MetrikaWindow
    if (w.__rcMetrikaLoaded || window.location.hostname === 'localhost') {
        return
    }
    w.__rcMetrikaLoaded = true
    // Очередь вызовов до загрузки tag.js — как в официальном сниппете.
    const ym: NonNullable<MetrikaWindow['ym']> =
        w.ym ||
        Object.assign(
            function () {
                const queue = (ym.a = ym.a || [])
                // eslint-disable-next-line prefer-rest-params
                queue.push(arguments)
            },
            { l: Date.now() }
        )
    w.ym = ym
    const script = document.createElement('script')
    script.async = true
    script.src = 'https://mc.yandex.ru/metrika/tag.js'
    document.head.appendChild(script)
    ym(METRIKA_ID, 'init', {
        clickmap: true,
        trackLinks: true,
        accurateTrackBounce: true,
        webvisor: true,
    })
}

/** Открыть баннер повторно (ссылка «Настройки cookie» в футере). */
export const openCookieSettings = () => {
    window.dispatchEvent(new Event(OPEN_EVENT))
}

export const CookieConsent: React.FC = React.memo(() => {
    const { language } = useLanguage()
    const t = translations[language] ?? translations.en
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        // В готовый HTML для поисковиков баннер не попадает.
        if (isPrerender()) return
        const choice = readChoice()
        if (choice === 'all') {
            loadMetrika()
        } else if (choice === null) {
            setVisible(true)
        }
        const reopen = () => setVisible(true)
        window.addEventListener(OPEN_EVENT, reopen)
        return () => window.removeEventListener(OPEN_EVENT, reopen)
    }, [])

    const handleChoice = useCallback((choice: Choice) => {
        saveChoice(choice)
        if (choice === 'all') {
            loadMetrika()
        }
        // Отказ после ранее данного согласия: уже загруженный в этой
        // вкладке счётчик выгрузить нельзя — он не загрузится со следующей
        // страницы/перезагрузки.
        setVisible(false)
    }, [])

    if (!visible) return null

    return (
        <div
            className={s.banner}
            role="dialog"
            aria-live="polite"
            aria-label="Cookies"
        >
            <p className={s.text}>
                {t.text}
                <Link to={NAV_LINK_NAMES.TERMS} className={s.link}>
                    {t.more}
                </Link>
            </p>
            <div className={s.actions}>
                <Button onClick={() => handleChoice('necessary')}>
                    {t.necessaryOnly}
                </Button>
                <Button type="primary" onClick={() => handleChoice('all')}>
                    {t.acceptAll}
                </Button>
            </div>
        </div>
    )
})
